import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { authService } from '../../services/api';
import './PublicLayout.css';

const PublicLayout = ({ children }) => {
  const isAuthenticated = authService.isAuthenticated();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef = useRef(null);
  const buttonRef = useRef(null);

  const handleLogout = () => {
    authService.logout();
    window.location.href = '/';
  };

  // Fermer la sidebar en cliquant en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidebarOpen]);

  return (
    <div className="public-layout">
      {/* Bouton flottant pour ouvrir la sidebar */}
      <AnimatePresence>
        {!sidebarOpen && (
          <motion.button 
            ref={buttonRef}
            className="floating-sidebar-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Ouvrir le menu personnel"
            title="Espace Personnel"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0, rotate: 180 }}
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            🔐
          </motion.button>
        )}
      </AnimatePresence>

      {/* Overlay pour fermer la sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div 
            className="sidebar-overlay"
            onClick={() => setSidebarOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
        )}
      </AnimatePresence>

      {/* Sidebar pour le personnel */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.aside 
            ref={sidebarRef}
            className="public-sidebar open"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
        <div className="sidebar-header">
          <h3>🔐 Espace Personnel</h3>
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fermer le menu"
          >
            ✕
          </button>
        </div>
        
        <div className="sidebar-content">
          {isAuthenticated ? (
            <>
              <div className="user-info">
                <p>Connecté en tant que personnel</p>
              </div>
              <nav className="sidebar-nav">
                {[
                  { to: '/dashboard', icon: '📊', label: 'Tableau de bord' },
                  { to: '/patients', icon: '👥', label: 'Patients' },
                  { to: '/appointments', icon: '📅', label: 'Rendez-vous' },
                  { to: '/treatments', icon: '🦷', label: 'Traitements' },
                  { to: '/radiographies', icon: '📷', label: 'Radiographies' },
                  { to: '/invoices', icon: '💰', label: 'Factures' },
                  { to: '/staff', icon: '👨‍⚕️', label: 'Personnel' }
                ].map((item, index) => (
                  <motion.div
                    key={item.to}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link to={item.to} className="sidebar-link">
                      <motion.span 
                        className="link-icon"
                        whileHover={{ scale: 1.2, rotate: 5 }}
                      >
                        {item.icon}
                      </motion.span>
                      <span>{item.label}</span>
                    </Link>
                  </motion.div>
                ))}
              </nav>
              <div className="sidebar-footer">
                <button onClick={handleLogout} className="logout-btn">
                  <span className="link-icon">🚪</span>
                  <span>Déconnexion</span>
                </button>
              </div>
            </>
          ) : (
            <div className="login-section">
              <p className="login-prompt">Accès réservé au personnel</p>
              <Link to="/login" className="login-btn">
                <span className="link-icon">🔑</span>
                <span>Se connecter</span>
              </Link>
            </div>
          )}
        </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Contenu principal */}
      <motion.main 
        className="public-main"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {children}
      </motion.main>
    </div>
  );
};

export default PublicLayout;

